<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1>
          <font-awesome-icon icon="cog"/>
          {{ $t('title') }}
        </h1>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="search" :placeholder="$t('searchPlaceholder')"
                          style="min-width: 280px;"></b-form-input>
            <button v-if="search" @click="search = ''" class="btn btn-secondary mr-1 mb-1">
              <font-awesome-icon icon="times"/>&nbsp;{{ $t('clear') }}
            </button>
            <div class="ml-auto text-muted small align-self-center">
              {{ $t('found', [visibleCount]) }} / {{ totalDefined }}
            </div>
          </div>
        </div>
      </div>

      <div class="col-12" v-if="geoUsage && ['owner','admin'].includes($store.state.user.role)">
        <div class="tile">
          <h4 class="tile-title mb-2">
            <font-awesome-icon icon="map-marker-alt"/>&nbsp;Gợi ý địa chỉ — dùng trong tháng {{ geoUsage.yearMonth }} (toàn hệ thống · key chung)
          </h4>
          <div v-for="(u, prov) in geoUsage.providers" :key="prov" class="d-flex flex-wrap align-items-center mb-1" style="gap: 22px;">
            <div style="min-width: 68px"><b class="text-uppercase">{{ prov }}</b></div>
            <div><span class="text-muted small">Chi tiết (tính tiền):</span> <strong>{{ u.placeDetail }}</strong><span v-if="u.freeQuota" class="text-muted small"> / {{ u.freeQuota }} free</span></div>
            <div><span class="text-muted small">Gợi ý:</span> <strong>{{ u.autocomplete }}</strong></div>
            <div><span class="text-muted small">Ước phí:</span> <strong :class="u.estCostVnd > 0 ? 'text-danger' : ''">{{ (u.estCostVnd || 0).toLocaleString('vi-VN') }} đ</strong></div>
          </div>
          <div v-if="!Object.keys(geoUsage.providers || {}).length" class="text-muted small">Chưa có lượt gọi tháng này.</div>
          <div v-if="geoUsage.topClients && geoUsage.topClients.length" class="mt-2 small text-muted">
            Top khách: <span v-for="(c, i) in geoUsage.topClients" :key="i">{{ c.client }} ({{ c.count }})<span v-if="i < geoUsage.topClients.length - 1">, </span></span>
          </div>
        </div>
      </div>

      <div class="col-12" v-for="group in visibleGroups" :key="group.title">
        <div class="tile">
          <h4 class="tile-title mb-3 d-flex align-items-center">
            <span><font-awesome-icon :icon="group.icon"/>&nbsp;{{ group.title }}</span>
            <span v-if="group.wesignActions && canGetSid" class="ml-auto">
              <button class="btn btn-sm btn-outline-success" @click="openGetSidModal"
                      title="Lấy SID từ MISA AMIS Platform (TTL 1 phút) → đổi sang Access Token">
                <font-awesome-icon icon="key"/>&nbsp;Lấy SID
              </button>
            </span>
          </h4>
          <!-- Readonly fields: compact inline list, 1 dòng / item -->
          <div v-if="readonlyItems(group).length" class="etc-readonly-list mb-3">
            <div class="etc-readonly-item" v-for="item in readonlyItems(group)" :key="item.key">
              <span class="etc-readonly-item__label">
                <font-awesome-icon icon="sync-alt" class="text-muted mr-1" :title="item.help || 'Tự cập nhật'"/>
                {{ item.label }}:
              </span>
              <span v-if="draft[item.key]" class="etc-readonly-item__value text-monospace">
                {{ formatReadonlyValue(item, draft[item.key]) }}
              </span>
              <span v-else class="etc-readonly-item__value text-muted font-italic">{{ $t('notSet') }}</span>
            </div>
          </div>

          <!-- Editable rows -->
          <div class="etc-row" v-for="item in editableItems(group)" :key="item.key">
            <div class="etc-row__label">
              <div class="etc-row__label-text">{{ item.label }}</div>
              <div class="etc-row__label-hint">
                <span class="badge badge-light">{{ item.key }}</span>
                <span v-if="item.help" class="ml-2" style="white-space: pre-line;">{{ item.help }}</span>
              </div>
            </div>
            <div class="etc-row__control">
              <!-- boolean -->
              <b-form-checkbox v-if="item.type === 'boolean'" switch size="lg"
                               :checked="draft[item.key] === '1'"
                               :disabled="!canEdit(item)"
                               @change="onBoolChange(item, $event)">
                <span class="small text-muted">
                  {{ draft[item.key] === '1' ? $t('on') : $t('off') }}
                </span>
              </b-form-checkbox>
              <!-- number -->
              <b-input-group v-else-if="item.type === 'number'">
                <b-form-input type="number" v-model="draft[item.key]"
                              :disabled="!canEdit(item)" :placeholder="$t('notSet')"></b-form-input>
                <b-input-group-append v-if="item.unit" is-text>{{ item.unit }}</b-input-group-append>
              </b-input-group>
              <!-- textarea -->
              <b-form-textarea v-else-if="item.type === 'textarea'" rows="3" max-rows="10"
                               v-model="draft[item.key]" :disabled="!canEdit(item)"
                               :placeholder="$t('notSet')"></b-form-textarea>
              <!-- datetime: chọn ngày giờ, LƯU vào Etc dưới dạng epoch ms (chuỗi số) -->
              <b-input-group v-else-if="item.type === 'datetime'">
                <b-form-input type="datetime-local" step="60"
                              :value="msToLocalInput(draft[item.key])"
                              :disabled="!canEdit(item)"
                              @input="onDateTimeChange(item, $event)"></b-form-input>
                <b-input-group-append is-text>
                  <span class="small text-muted">
                    {{ draft[item.key] ? draft[item.key] + ' ms' : $t('notSet') }}
                  </span>
                </b-input-group-append>
              </b-input-group>
              <!-- password: bí mật (token/secret) → che, bấm mắt mới xem -->
              <b-input-group v-else-if="item.type === 'password'">
                <b-form-input :type="revealed[item.key] ? 'text' : 'password'"
                              v-model="draft[item.key]" :disabled="!canEdit(item)"
                              autocomplete="new-password"
                              :placeholder="$t('notSet')"></b-form-input>
                <b-input-group-append>
                  <b-button variant="outline-secondary" class="is-border-1"
                            @click="$set(revealed, item.key, !revealed[item.key])">
                    <i :class="revealed[item.key] ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <!-- text -->
              <b-input-group v-else>
                <b-form-input v-model="draft[item.key]" :disabled="!canEdit(item)"
                              :placeholder="$t('notSet')"></b-form-input>
                <b-input-group-append v-if="item.unit" is-text>{{ item.unit }}</b-input-group-append>
              </b-input-group>
              <!-- Mốc do phonenet-data TỰ ĐO. Lệch với giá trị đang dùng = có người sửa tay → cảnh báo. -->
              <div v-if="item.auto && autoValue(item)" class="small mt-1">
                <span class="text-muted">Số đo từ phonenet-data:</span>
                <strong>{{ fmtMs(autoValue(item)) }}</strong>
                <template v-if="String(draft[item.key] || '') !== String(autoValue(item))">
                  <b-badge variant="warning" class="ml-1">đang dùng giá trị khác</b-badge>
                  <b-link class="ml-1" @click="$set(draft, item.key, String(autoValue(item)))">Dùng số đo này</b-link>
                </template>
                <b-badge v-else variant="success" class="ml-1">khớp</b-badge>
              </div>
            </div>
            <div class="etc-row__action">
              <button v-if="canEdit(item) && isDirty(item)"
                      @click="saveItem(item)" class="btn btn-sm btn-primary">
                <font-awesome-icon icon="save"/>&nbsp;{{ $t('save') }}
              </button>
              <button v-if="canEdit(item) && isDirty(item)"
                      @click="resetItem(item)" class="btn btn-sm btn-light ml-1">
                {{ $t('cancel') }}
              </button>
              <span v-else-if="!canEdit(item)" class="text-muted small">
                <font-awesome-icon icon="lock"/>
              </span>
              <template v-else-if="hasStored(item)">
                <span class="text-success small"><font-awesome-icon icon="check"/></span>
                <button v-if="canDelete(item)" @click="delItem(item)"
                        class="btn btn-sm btn-outline-danger is-border-1 ml-1" :title="$t('delete')">
                  <font-awesome-icon icon="trash"/>
                </button>
              </template>
              <span v-else class="text-muted small">{{ $t('notSet') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Unknown keys (in DB but not defined in groups) -->
      <div class="col-12" v-if="unknownItems.length && showAdvanced">
        <div class="tile">
          <h4 class="tile-title mb-3">
            <font-awesome-icon icon="exclamation-triangle" class="text-warning"/>
            &nbsp;{{ $t('advanced') }}
            <small class="text-muted">({{ unknownItems.length }})</small>
          </h4>
          <div class="etc-row" v-for="item in unknownItems" :key="item._id">
            <div class="etc-row__label">
              <div class="etc-row__label-text">{{ item.label }}</div>
              <div class="etc-row__label-hint">
                <span class="badge badge-warning">{{ item.key }}</span>
              </div>
            </div>
            <div class="etc-row__control">
              <b-form-textarea v-model="item.value" rows="2" max-rows="8"></b-form-textarea>
            </div>
            <div class="etc-row__action">
              <button @click="saveRaw(item)" class="btn btn-sm btn-primary">
                <font-awesome-icon icon="save"/>&nbsp;{{ $t('save') }}
              </button>
              <button @click="delRaw(item._id)" class="btn btn-sm btn-danger ml-1">
                <font-awesome-icon icon="trash"/>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12" v-if="unknownItems.length">
        <button @click="showAdvanced = !showAdvanced" class="btn btn-link">
          <font-awesome-icon :icon="showAdvanced ? 'chevron-up' : 'chevron-down'"/>
          &nbsp;{{ showAdvanced ? $t('hideAdvanced') : $t('showAdvanced', [unknownItems.length]) }}
        </button>
        <button v-if="canCreateRaw" @click="openCreate" class="btn btn-link">
          <font-awesome-icon icon="plus"/>&nbsp;{{ $t('create') }}
        </button>
      </div>
    </div>

    <b-modal v-model="showForm" :title="$t('create')" @ok="saveNew" no-close-on-backdrop no-enforce-focus>
      <form-input :label="$t('label')" :model="formModel" attribute="label" :errors="formErrors"></form-input>
      <form-input :label="$t('key')" :model="formModel" attribute="key" :errors="formErrors"></form-input>
      <form-textarea :label="$t('value')" :model="formModel" attribute="value" :errors="formErrors"></form-textarea>
    </b-modal>

    <get-sid-modal ref="getSidModal" @done="onGetSidDone"></get-sid-modal>
  </main>
</template>

<script>
import GetSidModal from './setting/GetSidModal.vue';

export default {
  components: { GetSidModal },
  data: function () {
    return {
      service: '/etc',
      items: [],
      draft: {},
      revealed: {},   // key nào đang được bấm "con mắt" để lộ giá trị (type: password)
      search: '',
      showAdvanced: false,
      showForm: false,
      formModel: {},
      formErrors: [],
      geoUsage: null,
      hiddenKeys: ['bot-train-fee', 'bot-chat-fee', 'bot-answer-fee', 'bot-question-fee', 'bot-teach-fee', 'zalo-server'],
      groups: [
        {
          title: 'Mobile App', icon: 'mobile-alt',
          items: [
            {key: 'mobile-ios-version', label: 'Phiên bản App iOS hiện tại', type: 'text', role: ['owner', 'admin', 'tech']},
            {key: 'mobile-android-version', label: 'Phiên bản App Android hiện tại', type: 'text', role: ['owner', 'admin', 'tech']},
          ]
        },
        {
          title: 'Phí tin nhắn & gọi', icon: 'envelope',
          items: [
            {key: 'zns-fee', label: 'Phí tin nhắn ZBS', type: 'number', unit: 'đ/tin', role: ['owner', 'admin']},
            {key: 'viber-fee', label: 'Phí tin nhắn Viber', type: 'number', unit: 'đ/tin', role: ['owner', 'admin']},
            {key: 'sms-ads-fee', label: 'Phí tin nhắn QC', type: 'number', unit: 'đ/tin', help: 'SMS quảng cáo', role: ['owner', 'admin']},
            {key: 'sms-care-fee', label: 'Phí tin nhắn CSKH', type: 'number', unit: 'đ/tin', help: 'SMS chăm sóc khách hàng', role: ['owner', 'admin']},
            {key: 'ai-voice-token-per-stt-sec', label: 'Gọi AI — quy đổi nhận dạng ra token', type: 'number', unit: 'token/giây', help: 'GIÁ BÁN vẫn là "Giá BÁN /1M token" chung của cả hệ thống — đây chỉ là hệ số quy đổi.\n\nMột giây nghe khách nói tính bằng bao nhiêu token. Chỉnh khi đo được chi phí GPU thật.\n\nĐo hiện tại: mỗi cuộc gọi tốn 1–7 giây nhận dạng.', role: ['owner', 'admin']},
            {key: 'ai-voice-token-per-tts-1k', label: 'Gọi AI — quy đổi máy đọc ra token', type: 'number', unit: 'token/1.000 ký tự', help: 'GIÁ BÁN vẫn là "Giá BÁN /1M token" chung — đây chỉ là hệ số quy đổi.\n\nCHỈ tính câu sinh tại chỗ: câu có biến {{tên}} và câu AI tự nói. Câu tĩnh đã đọc sẵn một lần lúc lưu kịch bản nên KHÔNG tính vào từng cuộc.', role: ['owner', 'admin']},
          ]
        },
        {
          title: 'Gói cước & dịch vụ', icon: 'box',
          items: [
            {key: 'plan-fee', label: 'Giá gói cước thoại trọn gói', type: 'number', unit: 'đ/tháng', role: ['owner', 'admin']},
            {key: 'cdp-fee', label: 'Giá CDP', type: 'number', unit: 'đ/khách/tháng', role: ['owner', 'admin']},
            {key: 'cdp-limit', label: 'Ngưỡng khách bắt đầu tính phí CDP', type: 'number', unit: 'khách', help: 'Dưới ngưỡng này thì miễn phí', role: ['owner', 'admin']},
            {key: 'restore-fee', label: 'Giá khôi phục file ghi âm', type: 'number', unit: 'đ/file', role: ['owner', 'admin']},
            {key: 'chatbot-fee', label: 'Giá nhân viên ảo', type: 'number', unit: 'đ/tháng', role: ['owner', 'admin', 'tech']},
            {key: 'bot-speed-to-text-gpt-fee', label: 'Giá speech-to-text GPT', type: 'number', unit: 'đ/phút', role: ['owner', 'admin']},
          ]
        },
        {
          title: 'Nhân viên ảo (AI Agent)', icon: 'robot',
          items: [
            // 3 key cốt lõi business — các tham số tuning khác (vector threshold, cache TTL,
            // hard cap token, FAQ limit, policy ask loop) đã hardcode trong common helper
            // (default hợp lý, không cần admin chỉnh thường xuyên).
            {key: 'ai_agent_enabled', label: 'Bật tính năng AI Agent', type: 'boolean', help: 'TẮT = mọi bot AI Agent KHÔNG trả lời (UI khách hiện banner "Tạm dừng").\nBật trước khi cho khách kích hoạt.', role: ['owner', 'admin', 'tech']},
            {key: 'ai_agent_free_token_monthly', label: 'Token miễn phí TẶNG MỖI THÁNG / client', type: 'number', unit: 'token', help: 'KT-32990 — mỗi khách được tặng lại số token này vào đầu mỗi tháng (theo múi giờ khách).\n\n• KHÔNG cộng dồn: phần chưa dùng của tháng trước sẽ mất.\n• Chỉ cấp cho khách ĐÃ kích hoạt Nhân viên AI hoặc đã từng nhận token miễn phí.\n• Token cấp khi khách phát sinh dùng AI (không quét cấp trước hàng loạt).\n• Hết token mà khách chưa đồng ý trừ Số dư → AI dừng trả lời, KHÔNG tự trừ tiền.\n\nĐẶT 0 = TẮT chương trình tặng (số dư đã cấp vẫn dùng tiếp). Không cần deploy.\nĐổi giá trị có độ trễ tối đa 30 giây (bộ đệm cấu hình).\n\nVÍ DỤ: 200.000 token ≈ 285 lượt AI trả lời ≈ 40.000đ theo giá bán hiện hành.', role: ['owner', 'admin']},
            {key: 'ai_agent_free_token', label: 'Token miễn phí cấp 1 lần / client (NGỪNG DÙNG)', type: 'number', unit: 'token', help: '⚠️ NGỪNG DÙNG từ 2026-08-23 (KT-32990) — thay bằng khoá "Token miễn phí TẶNG MỖI THÁNG" ở trên. Giữ lại để tra dữ liệu cũ.\n\nCơ chế cũ: cấp 1 LẦN khi khách bấm "Kích hoạt" (snapshot Client.aiAgentFreeTokenInitial/Remaining).\nKHÔNG refill theo tháng. Tiêu hết → khách phải mua gói hoặc bật postpaid.\nĐổi giá trị chỉ áp dụng cho khách kích hoạt MỚI (khách đã kích hoạt giữ số đã cấp).\n\nVÍ DỤ:\n• 100.000 token ≈ 28 câu trả lời (đủ thử nghiệm)\n• 1.000.000 token ≈ 285 câu (gói thử rộng rãi)', role: ['owner', 'admin']},
            // ===== v2 (2026-06-05): 1 GIÁ FLAT DUY NHẤT + provider chọn + giá vốn để báo cáo =====
            {key: 'ai_token_price_per_1m', label: 'Giá BÁN /1M token', type: 'number', unit: 'VND/1M token', help: 'Giá KHÁCH trả cho 1 triệu token — ÁP CHO TẤT CẢ model (gpt-4o / gpt-4o-mini / deepseek-v4-flash).\n\nKHÔNG tách input/output, KHÔNG tách main/mini.\n\nÁp cho:\n• AI Agent chat (gpt-4o / deepseek)\n• Conversation QA (chấm điểm hội thoại)\n• Fast-path mini (smalltalk + faq-match)\n• Eval + Optimization (đánh giá + tối ưu bot)\n• Phần token vượt cap gói (fallback creditBalance)\n\nMẶC ĐỊNH: 200.000 VND/1M token.\n4 gói cố định tính theo giá này:\n• Basic 200k / 1M (0% discount)\n• Starter 1.9M / 10M (5%)\n• Pro 3.6M / 20M (10%)\n• Ultimate 7.65M / 45M (15%)\n\nCHỈ OWNER xem/sửa được.', role: ['owner']},
            {key: 'ai_provider', label: 'LLM Provider', type: 'string', help: 'Chọn provider LLM toàn hệ thống:\n• "openai" — gpt-4o (main) + gpt-4o-mini (mini) — chất lượng cao, giá cao\n• "deepseek" — deepseek-v4-flash — chất lượng tốt, giá rẻ ~9× OpenAI main\n\nFAIL handling: provider lỗi → trả backupAnswer cho khách + bắn alert kênh tech (Kafka slack-system-submitted group alert-tech, KHÔNG fallback tự động).\nĐổi value → cache LLM tự reload sau 30s.\n\nCHỈ OWNER xem/sửa được.', role: ['owner']},
            {key: 'ai_agent_openai_api_key', label: 'OpenAI API key', type: 'password', help: 'API key OpenAI (gpt-4o + gpt-4o-mini + embeddings fallback).\nLấy tại: https://platform.openai.com/api-keys\n\nLƯU Ý:\n• Key bắt đầu "sk-..."\n• Đổi key → cache LLM tự reload sau 30s\n• CHỈ OWNER xem được', role: ['owner']},
            {key: 'deepseek_api_key', label: 'DeepSeek API key', type: 'password', help: 'API key DeepSeek (deepseek-v4-flash). Cần khi ai_provider=deepseek.\nLấy tại: https://platform.deepseek.com/api_keys\n\nLƯU Ý: KHÔNG cần nếu chỉ dùng OpenAI. CHỈ OWNER xem được.', role: ['owner']},
            // Alert LLM fail → tự bắn qua Kafka topic 'slack-system-submitted' group 'alert-tech' (cơ chế chung), KHÔNG cần Etc webhook URL.
            // ===== Giá VỐN cho báo cáo lỗ-lãi admin (KHÔNG ảnh hưởng billing khách) =====
            {key: 'openai_main_input_cost_per_1m', label: '[Vốn] OpenAI gpt-4o input /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn input token gpt-4o (chỉ dùng báo cáo lỗ-lãi).\n\nOpenAI charge: $2.50/1M ≈ 62.000đ.\nĐổi số này KHÔNG ảnh hưởng billing khách.', role: ['owner']},
            {key: 'openai_main_output_cost_per_1m', label: '[Vốn] OpenAI gpt-4o output /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn output token gpt-4o. OpenAI charge: $10/1M ≈ 250.000đ.', role: ['owner']},
            {key: 'openai_mini_input_cost_per_1m', label: '[Vốn] OpenAI mini input /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn input gpt-4o-mini. OpenAI charge: $0.15/1M ≈ 4.000đ.', role: ['owner']},
            {key: 'openai_mini_output_cost_per_1m', label: '[Vốn] OpenAI mini output /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn output gpt-4o-mini. OpenAI charge: $0.60/1M ≈ 15.000đ.', role: ['owner']},
            {key: 'deepseek_flash_input_cost_per_1m', label: '[Vốn] DeepSeek v4-flash input /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn input deepseek-v4-flash. DeepSeek charge ≈ $0.27/1M ≈ 7.000đ.\nDeepSeek chỉ có 1 model — áp cho cả main + mini.', role: ['owner']},
            {key: 'deepseek_flash_output_cost_per_1m', label: '[Vốn] DeepSeek v4-flash output /1M', type: 'number', unit: 'VND/1M', help: 'Giá vốn output deepseek-v4-flash. DeepSeek charge ≈ $1.10/1M ≈ 28.000đ.', role: ['owner']},
            {key: 'ai_agent_custom_embed_api_key', label: 'Custom Embed API key (Gadget BGE-M3)', type: 'password', help: 'API key cho service embedding nội bộ bgem3.gadget.com.vn (Vietnamese BGE-M3).\nĐược ưu tiên cao nhất trong chuỗi fallback (custom → FPT → HF → OpenAI).\n\nCHỈ OWNER mới xem/sửa.', role: ['owner']},
            {key: 'ai_agent_fpt_cloud_api_key', label: 'FPT Cloud API key', type: 'password', help: 'API key của FPT Cloud Marketplace để dùng model "Vietnamese_Embedding" (AITeamVN).\nLấy tại: https://marketplace.fptcloud.com/vi/ai-product/AITeamVN/Vietnamese_Embedding\n\nFallback level 2 (sau Custom Embed).\nCHỈ OWNER mới xem/sửa.', role: ['owner']},
            {key: 'ai_agent_hf_api_key', label: 'HuggingFace API key', type: 'password', help: 'API key HuggingFace để dùng model BAAI/bge-m3 (đa ngôn ngữ).\nLấy tại: https://huggingface.co/settings/tokens\n\nFallback level 3 (sau FPT Cloud).\nCHỈ OWNER mới xem/sửa.', role: ['owner']},
          ]
        },
        {
          title: 'AI Trợ lý nội bộ (AI Assistant)', icon: 'headset',
          items: [
            {key: 'ai_assistant_enabled', label: 'Bật Trợ lý AI nội bộ', type: 'boolean', help: 'Kill-switch TOÀN HỆ THỐNG cho Trợ lý AI nội bộ (widget chat hỗ trợ nhân viên trong dashboard).\nTÁCH RỜI gói "nhân viên ảo" (chatbot khách) — Trợ lý nội bộ MIỄN PHÍ, Callio chịu phí token.\n\n• BẬT = mọi client thấy widget Trợ lý + dùng được (giới hạn bởi trần token/ngày bên dưới).\n• TẮT (mặc định) = ẩn widget + chặn gửi tin toàn hệ thống.\nÁp dụng sau ~30s (cache). KHÔNG còn phụ thuộc hạn gói nhân viên ảo.', role: ['owner', 'admin', 'tech']},
            {key: 'ai_assistant_daily_token_limit', label: 'Trần token / ngày / client', type: 'number', unit: 'token/ngày', help: 'Trần token AI Trợ lý nội bộ cho MỖI client trong 1 ngày (reset theo timezone client).\nChống spam đốt tiền — Callio chịu phí token nên cần trần an toàn.\nChạm trần → client bị CHẶN tới hết ngày + bắn Slack kênh tech.\n\n• 0 = KHÔNG giới hạn (rủi ro nếu bị tấn công)\n• Mặc định: 2.000.000 token/ngày/client (~250-600 câu hỏi)\nÁp dụng sau ~30s (cache).', role: ['owner', 'admin', 'tech']},
            {key: 'ai_assistant_daily_request_limit', label: 'Trần số yêu cầu / ngày / client', type: 'number', unit: 'yêu cầu/ngày', help: 'Trần SỐ yêu cầu (tin nhắn) AI Trợ lý nội bộ cho MỖI client trong 1 ngày (reset theo timezone client).\nTuyến phòng thủ chống spam theo SỐ LƯỢNG (rẻ hơn để chặn flood).\nChạm trần → client bị CHẶN tới hết ngày + bắn Slack kênh tech.\n\n• 0 = KHÔNG giới hạn\n• Mặc định: 1.000 yêu cầu/ngày/client\nÁp dụng sau ~30s (cache).', role: ['owner', 'admin', 'tech']},
          ]
        },
        {
          title: 'AI đánh giá hội thoại', icon: 'star-half-alt',
          items: [
            {key: 'conversation_eval_enabled', label: 'Bật tính năng AI đánh giá hội thoại', type: 'boolean', help: 'Kill-switch toàn hệ thống cho tính năng AI đánh giá hội thoại (call/chat).\nTẮT = ngừng submit hội thoại đi đánh giá (áp dụng sau ~30s do cache).\nBật trước khi cho client dùng.\n\nBilling: mỗi client MIỄN PHÍ 7 ngày kể từ lần đầu bật, sau đó tự tính phí theo token (gói AI nếu có, không thì trừ số dư) — KHÔNG còn cờ bật/tắt tính phí toàn hệ thống.', role: ['owner', 'admin', 'tech']},
            {key: 'eval_upsell_banner_enabled', label: 'Hiện banner chào mời bật đánh giá cuộc gọi', type: 'boolean', help: 'TẮT (mặc định) = KHÔNG hiện banner.\nBẬT = client đang gọi nhiều nhưng CHƯA bật AI đánh giá cuộc gọi sẽ thấy banner mời kích hoạt (kèm ước chi phí/tuần, tính sẵn bởi job eval-upsell-estimate). Áp dụng sau ~30s (cache).', role: ['owner', 'admin', 'tech']},
          ]
        },
        {
          title: 'Gọi AI (chiến dịch gọi tự động)', icon: 'phone-volume',
          items: [
            {key: 'ai-voice-tts-url', label: 'Địa chỉ dịch vụ sinh tiếng nói', type: 'text', help: 'Dịch vụ tts-api trên máy GPU, cổng 8801. Ví dụ: https://ai-voice.phonenet.io/tts-api\nDùng cho cả lúc soạn kịch bản (nghe thử, đọc sẵn) và lúc đang gọi.\nĐể trống = dùng http://127.0.0.1:8801, chỉ đúng khi chạy chung máy GPU.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-stt-url', label: 'Địa chỉ dịch vụ nhận dạng tiếng nói', type: 'text', help: 'Dịch vụ stt-api trên máy GPU, cổng 8802. Ví dụ: https://ai-voice.phonenet.io/stt-api\nNghe câu khách vừa nói, ngay trong cuộc gọi.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-llm-url', label: 'Địa chỉ mô hình ngôn ngữ tại chỗ', type: 'text', help: 'vLLM trên máy GPU. Mặc định http://127.0.0.1:8000/v1', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-llm-model', label: 'Tên mô hình ngôn ngữ', type: 'text', help: 'Tên model khai trong vLLM. Mặc định: call-eval', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-api-key', label: 'Khoá gọi dịch vụ giọng nói', type: 'password', help: 'Gửi kèm ở tiêu đề X-API-Key. Bắt buộc khi gọi qua tên miền ai-voice.phonenet.io.\nThiếu khoá thì nhận 403 từ nginx — không phải lỗi mô hình.\nĐể trống nếu gọi thẳng vào máy GPU trong mạng nội bộ.', role: ['owner', 'admin', 'tech']},

            {key: 'ai-voice-socket-host', label: 'Địa chỉ tổng đài giao kênh cho AI', type: 'text', help: 'Tổng đài FreeSWITCH nối kênh vào đây để AI điều khiển cuộc gọi.\nĐây là giá trị MẶC ĐỊNH. Máy tổng đài nào khai biến môi trường AI_VOICE_SOCKET_HOST thì máy đó dùng giá trị riêng — bộ gọi chạy trên nhiều máy, mỗi máy một địa chỉ.\nĐổi xong PHẢI khởi động lại tiến trình ai-caller.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-socket-port', label: 'Cổng nhận kênh từ tổng đài', type: 'number', help: 'Mặc định 8040. Máy nào khai AI_VOICE_SOCKET_PORT thì dùng giá trị riêng của máy đó.\nĐổi xong PHẢI khởi động lại ai-caller thì tiến trình mới nghe ở cổng mới.', role: ['owner', 'admin', 'tech']},

            {key: 'ai-voice-max-concurrent', label: 'Số cuộc gọi AI chạy cùng lúc trên một máy', type: 'number', unit: 'cuộc', help: 'Đặt theo sức GPU, KHÔNG theo sức tổng đài — nút cổ chai là card đồ hoạ.\nMặc định 10.', role: ['owner', 'admin', 'tech']},

            {key: 'ai-voice-rec-silence-hits', label: 'Số nhịp im lặng thì dừng ghi câu khách nói', type: 'number', help: 'Thấp quá = cắt lời khách giữa câu. Cao quá = khách nói xong phải chờ.\nMặc định 2. Đây là tham số cần chỉnh nhiều nhất khi chạy đường thật.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-rec-silence-thresh', label: 'Ngưỡng coi là im lặng', type: 'number', help: 'Đường nhiễu thì nâng lên. Mặc định 200.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-rec-max-sec', label: 'Trần thời gian ghi một lượt khách nói', type: 'number', unit: 'giây', help: 'Mặc định 20.', role: ['owner', 'admin', 'tech']},

            {key: 'ai-voice-llm-timeout-ms', label: 'Quá hạn chờ mô hình ngôn ngữ', type: 'number', unit: 'ms', help: 'Mặc định 4000. Quá hạn thì cuộc gọi đi nhánh dự phòng thay vì treo máy.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-tts-timeout-ms', label: 'Quá hạn chờ sinh tiếng nói', type: 'number', unit: 'ms', help: 'Mặc định 5000.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-stt-timeout-ms', label: 'Quá hạn chờ nhận dạng tiếng nói', type: 'number', unit: 'ms', help: 'Mặc định 8000.', role: ['owner', 'admin', 'tech']},

            {key: 'ai-voice-llm-max-tokens', label: 'Độ dài tối đa một câu trả lời của AI', type: 'number', help: 'Mặc định 160. Dài quá thì khách phải nghe lâu.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-llm-temperature', label: 'Độ ngẫu nhiên khi AI trả lời', type: 'number', help: '0 là máy móc, 1 là bay bổng. Mặc định 0.3.', role: ['owner', 'admin', 'tech']},

            {key: 'ai-voice-prerender-timeout-ms', label: 'Quá hạn đọc sẵn cả kịch bản', type: 'number', unit: 'ms', help: 'Chạy khi người dùng lưu kịch bản. Kịch bản dài thì cần nới. Mặc định 120000.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-preview-timeout-ms', label: 'Quá hạn nghe thử một câu', type: 'number', unit: 'ms', help: 'Người dùng đang chờ nên đừng để lâu. Mặc định 15000.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-exec-timeout-ms', label: 'Quá hạn chờ tổng đài thực hiện một lệnh', type: 'number', unit: 'ms', help: 'Phòng khi kênh chết mà không báo. Mặc định 60000.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-dial-timeout-ms', label: 'Quá hạn chờ tổng đài giao kênh sau khi quay số', type: 'number', unit: 'ms', help: 'Quá hạn này coi như quay số hỏng. Mặc định 60000.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-requeue-delay-ms', label: 'Chờ trước khi trả việc về hàng đợi khi hết chỗ', type: 'number', unit: 'ms', help: 'Tránh quay hàng đợi liên tục khi đã chạm van đồng thời. Mặc định 5000.', role: ['owner', 'admin', 'tech']},
            {key: 'ai-voice-stt-upload', label: 'Gửi nội dung file ghi âm qua mạng', type: 'number', help: '1 = gửi cả nội dung file; 0 = chỉ gửi đường dẫn (mặc định, nhanh hơn, dùng khi console và máy GPU thấy chung thư mục ghi âm).', role: ['owner', 'admin', 'tech']},
          ]
        },
        {
          title: 'Giới thiệu', icon: 'user-plus',
          items: [
            {key: 'ref-amount', label: 'Tiền cộng khi giới thiệu khách mới', type: 'number', unit: 'đ', help: 'Cộng cho người giới thiệu', role: ['owner', 'admin']},
            {key: 'ref-target', label: 'Tiền tặng khách mới từ link giới thiệu', type: 'number', unit: 'đ', role: ['owner', 'admin']},
            {key: 'ref-deposit', label: 'Số tiền nạp tối thiểu của khách được giới thiệu', type: 'number', unit: 'đ', help: 'Đạt ngưỡng mới được cộng tiền ref', role: ['owner', 'admin']},
          ]
        },
        {
          title: 'Hệ thống', icon: 'cogs',
          items: [
            {key: 'kyc-time', label: 'Thời gian duyệt hồ sơ', type: 'number', unit: 'phút', role: ['owner', 'admin', 'tech']},
            {key: 'invoice-enabled', label: 'Tự động xuất hoá đơn', type: 'boolean', role: ['owner', 'admin']},
            {key: 'client-invoice', label: 'Mở tính năng mua/xuất hoá đơn điện tử (VAT)', type: 'boolean', role: ['owner', 'admin', 'tech']},
            {key: 'ecommerce', label: 'Tính năng mua sắm', type: 'boolean', role: ['owner', 'admin', 'tech']},
            {key: 'system-alert', label: 'Bật thông báo hệ thống', type: 'boolean', role: ['owner', 'admin', 'tech']},
            {key: 'social-webhook-log-sample-rate', label: 'Tỉ lệ ghi log webhook MXH thành công',
              type: 'number', unit: '(0..1)',
              help: 'Mặc định: dev=1 (log 100%), production=0.05 (5%). Set 0 để chỉ log lỗi. Lỗi luôn ghi 100%. Đổi có hiệu lực sau ~60s, không cần restart.',
              role: ['owner', 'admin', 'tech']},
            {key: 'facebook-token-validate-enabled', label: 'Kiểm tra hạn token Facebook',
              type: 'boolean',
              help: 'BẬT = cron quét debug_token các trang Facebook để cập nhật hạn token + cảnh báo sớm cho owner/người tạo kết nối.\nTẮT = tạm dừng kiểm tra (không cập nhật hạn, không gắn nhãn hết hạn, không gửi cảnh báo).\nMặc định khi chưa set = TẮT. Đổi có hiệu lực ở lần cron chạy kế tiếp.',
              role: ['owner', 'admin', 'tech']},
            {key: 'postmark_account_token', label: 'Postmark Account API Token', type: 'password',
              help: 'Token CẤP ACCOUNT của Postmark — để tạo/xác nhận email gửi riêng của đối tác (Sender Signature) cho white-label email.\n\nKHÁC Server token đang dùng để gửi mail.\n\nLẤY TẠI: https://account.postmarkapp.com/api_tokens\n(Đăng nhập Postmark → góc phải tên Account → "API Tokens" → mục "Account API Tokens" → copy/tạo token)\n\nĐể trống → email đối tác vẫn gửi từ địa chỉ Callio (không gián đoạn). CHỈ OWNER xem/sửa được.',
              role: ['owner']},
          ]
        },
        {
          title: 'DB Health Monitor', icon: 'database',
          items: [
            {key: 'db-monitor-username', label: 'Username (clusterMonitor role)', type: 'text',
              help: 'User MongoDB có role "clusterMonitor" để worker phonenet-db-health-monitor sample serverStatus/currentOp + endpoint $indexStats chạy được. KHÔNG dùng user app chính (thiếu quyền).\n\nTạo user trên Mongo:\n  db.createUser({ user: "phonenet-monitor", pwd: "...", roles: [{ role: "clusterMonitor", db: "admin" }] })',
              role: ['owner']},
            {key: 'db-monitor-password', label: 'Password', type: 'password',
              help: 'Password của user db-monitor-username. KHÔNG share, chỉ owner xem/sửa. Đổi sẽ tự reconnect worker ở tick kế tiếp (~30s).',
              role: ['owner']},
          ]
        },
        {
          title: 'Homer / SIP Trace (Chẩn đoán cuộc gọi)', icon: 'phone',
          items: [
            {key: 'homer_url', label: 'Homer URL (siptrace)', type: 'text',
              help: 'URL hệ thống Homer siptrace. VD: https://siptrace.phonenet.io. Dùng cho tính năng "Chẩn đoán cuộc gọi".',
              role: ['owner']},
            {key: 'homer_user', label: 'Homer Username', type: 'text',
              help: 'Tài khoản đăng nhập Homer (siptrace). Chỉ owner xem/sửa.',
              role: ['owner']},
            {key: 'homer_pass', label: 'Homer Password', type: 'password',
              help: 'Mật khẩu Homer (siptrace). KHÔNG share, chỉ owner xem/sửa. Đổi có hiệu lực sau ~30s.',
              role: ['owner']},
          ]
        },
        {
          title: 'Data Lake / Athena (báo cáo >90 ngày)', icon: 'database',
          items: [
            {key: 'athena_access_key_id', label: 'Athena Access Key ID', type: 'password',
              help: 'AWS Access Key ID để truy vấn data lake (Athena/S3). Dùng cho báo cáo cuộc gọi / cước / giao dịch / chiến dịch >90 ngày (bảng *_archived).\n\nĐể trống → dùng biến môi trường ATHENA_*/AWS_* của service (fallback).\nĐổi có hiệu lực sau ~5 phút (cache) hoặc restart service. CHỈ OWNER xem/sửa được.',
              role: ['owner']},
            {key: 'athena_secret_access_key', label: 'Athena Secret Access Key', type: 'password',
              help: 'AWS Secret Access Key tương ứng. Bí mật — KHÔNG share. CHỈ OWNER xem/sửa được.\n\nLƯU Ý bảo mật: nếu key từng bị lộ (commit source...) phải xoay key mới trên AWS IAM rồi cập nhật tại đây.',
              role: ['owner']},
            {key: 'athena_region', label: 'Athena Region', type: 'text',
              help: 'Vùng AWS của Athena/S3. Mặc định: ap-southeast-1.', role: ['owner']},
            {key: 'athena_db', label: 'Athena Database', type: 'text',
              help: 'Tên database Athena (Glue catalog). Mặc định: phonenet.', role: ['owner']},
          ]
        },
        {
          title: 'Kho dữ liệu cũ tự host (phonenet-data)', icon: 'hdd',
          items: [
            {key: 'phonenet_data_enabled', label: 'Bật đọc phonenet-data', type: 'boolean',
              help: 'TẮT (mặc định) = mọi thứ chạy y như hiện tại: tab "Lưu trữ" chỉ đọc Data Lake (Athena/S3).\n\nBẬT = Callio đọc dữ liệu cuộc gọi / contact chiến dịch cũ từ phonenet-data (Parquet trên disk) cho phần MỚI HƠN mốc bên dưới, và chỉ hỏi Data Lake cho phần CŨ HƠN mốc.\n\n⚠️ CHỈ có tác dụng khi ĐỦ CẢ 4 mục trong nhóm này (bật + mốc + URL + token). Thiếu bất kỳ mục nào → tự động chạy như cũ.\n\n⚠️ QUAN TRỌNG: bật cờ này cũng TỰ KHOÁ 4 lệnh archive cũ bên phonenet-console (archive-call, archive-call-campaign-contact, old-call, old-campaign) — chúng sẽ từ chối chạy. Đây là chủ đích: tránh xoá dữ liệu khỏi MongoDB sớm, phá mất ân hạn 7 ngày.',
              role: ['owner', 'admin', 'tech']},
            {key: 'phonenet_data_from_time', label: 'Mốc chia dữ liệu (ép chung — tùy chọn)', type: 'datetime',
              help: '⚠️ VAN TAY — BÌNH THƯỜNG ĐỂ TRỐNG.\n\nĐể TRỐNG = Callio dùng mốc TỰ ĐO riêng cho từng loại (call, contact chiến dịch) — đây là cách ĐÚNG NHẤT. Mỗi loại có ranh giới riêng (bản ghi cũ nhất phonenet-data đang giữ), tự cập nhật.\n\nĐIỀN = ép MỘT mốc chung cho MỌI loại, đè lên mốc tự đo. Chỉ dùng khi cần rollback thủ công.\n\nRANH GIỚI: bản ghi TỪ mốc trở đi đọc từ phonenet-data (disk), cũ hơn đọc từ Data Lake. Đặt SAI là HỞ DỮ LIỆU (người dùng không thấy, không báo lỗi).\n\n(Mốc tự đo theo loại lưu ở khoá phonenet_data_from_time_auto dạng JSON — chỉ đọc.)\n\nLưu dưới dạng epoch milliseconds.',
              role: ['owner', 'admin', 'tech']},
            {key: 'phonenet_data_url', label: 'URL service phonenet-data', type: 'text',
              help: 'Địa chỉ nội bộ của service phonenet-data, ví dụ: http://100.84.109.24:6060\n\nKHÔNG có dấu / ở cuối (tự cắt). Phải gọi được từ máy chạy phonenet-client-api.\nĐể trống → dùng biến môi trường PHONENET_DATA_URL (fallback).',
              role: ['owner', 'admin', 'tech']},
            {key: 'phonenet_data_token', label: 'Token nội bộ phonenet-data', type: 'password',
              help: 'Phải TRÙNG KHỚP với INTERNAL_TOKEN trong file .env của service phonenet-data.\nSai token → gọi bị 401 → hệ thống tự suy biến về đọc Data Lake (không lỗi giao diện).\nĐể trống → dùng biến môi trường PHONENET_DATA_TOKEN (fallback).',
              role: ['owner']},
          ]
        },
        {
          title: 'Triển khai Production (GitLab CI/CD)', icon: 'rocket',
          items: [
            {key: 'gitlab_api_url', label: 'GitLab API URL', type: 'text',
              help: 'Endpoint GitLab REST API. Mặc định: https://gitlab.com/api/v4 (để trống = dùng mặc định này).\nDùng cho tính năng "Triển khai Production" (nút Merge lên production).', role: ['owner']},
            {key: 'gitlab_api_token', label: 'GitLab API token', type: 'password',
              help: 'Personal/Project Access Token scope "api" — dùng để tạo & merge MR đưa code lên nhánh production (CI/CD tự deploy).\n\n⚠️ Token này có quyền merge production TẤT CẢ repo cấu hình. Chỉ owner xem/sửa được.\nLấy tại: GitLab → Settings → Access Tokens (scope: api).\nĐể trống → dùng biến môi trường GITLAB_API_TOKEN của phonenet-admin-api.', role: ['owner']},
          ]
        },
        {
          title: 'Tích hợp MISA WeSign', icon: 'signature', wesignActions: true,
          items: [
            {key: 'esign-admin-enabled', label: 'Bật đẩy hợp đồng KYC lên MISA WeSign', type: 'boolean',
              help: 'Tắt = ẩn nút "Đẩy MISA" trong KYC detail. Doc đang chờ ký vẫn xử lý callback', role: ['owner']},
            {key: 'esign-admin-provider-name', label: 'Tên hiển thị provider', type: 'text',
              help: 'VD: MISA WeSign — Callio', role: ['owner']},
            {key: 'esign-admin-client-id', label: 'MISA Client ID', type: 'text',
              help: 'Cấp bởi MISA partner', role: ['owner']},
            {key: 'esign-admin-client-secret', label: 'MISA Client Secret', type: 'text',
              help: 'Bí mật — không share', role: ['owner']},
            {key: 'esign-admin-app-code', label: 'MISA App Code', type: 'text',
              help: 'Định danh app trong doc payload', role: ['owner']},
            {key: 'esign-admin-access-token', label: 'Access Token (chỉ xem)', type: 'textarea', readonly: true,
              help: 'Worker tự refresh — không sửa tay', role: ['owner', 'account']},
            {key: 'esign-admin-amis-sid', label: 'AMIS sid (chỉ xem)', type: 'textarea', readonly: true,
              help: 'Auto lấy từ button "Lấy SID" — không paste tay', role: ['owner', 'account']},
            {key: 'esign-admin-refresh-token', label: 'Refresh Token (chỉ xem)', type: 'textarea', readonly: true,
              help: 'Worker tự cập nhật', role: ['owner', 'account']},
            {key: 'esign-admin-token-expire-time', label: 'Hạn token (timestamp ms — chỉ xem)', type: 'number', readonly: true,
              help: 'Worker tự cập nhật', role: ['owner', 'account']},
          ]
        },
        {
          title: 'Gợi ý địa chỉ (Google / Goong)', icon: 'map-marker-alt',
          items: [
            {key: 'google_maps_api_key', label: 'Google Maps API key', type: 'password',
              help: 'API key Google Cloud (bật Places API New + billing) cho gợi ý địa chỉ khi tạo Đơn hàng / Vận chuyển.\n\nĐỂ TRỐNG → dùng Goong (nếu có), hoặc tắt gợi ý — ô địa chỉ vẫn nhập text bình thường (KHÔNG ảnh hưởng người dùng).\n\nƯU TIÊN: khai CẢ Google lẫn Goong → hệ thống dùng GOOGLE.\n\nTạo tại: Google Cloud Console → APIs & Services → Credentials → API key (bật Places API New + billing).\nNên giới hạn key: chỉ Places API + IP server. KHÁC với Google login (OAuth) — phải tạo API key mới trong cùng project.\nCHỈ OWNER xem/sửa được.',
              role: ['owner']},
            {key: 'goong_api_key', label: 'Goong API key (dự phòng)', type: 'password',
              help: 'API key Goong (goong.io) — dùng khi KHÔNG khai Google (ưu tiên Google nếu có cả 2).\nGoong chuyên địa chỉ VN, lưu toạ độ vĩnh viễn không giới hạn.\nLấy tại: https://account.goong.io → API Keys.\nĐể trống + không có Google → tắt gợi ý (ô địa chỉ nhập text như thường).\nCHỈ OWNER xem/sửa được.',
              role: ['owner']},
            {key: 'google_maps_monthly_cap', label: 'Trần Place Details / tháng (toàn hệ thống)', type: 'number', unit: 'lượt/tháng',
              help: '1 KEY DÙNG CHUNG mọi khách. Tổng lượt Place Details (tính tiền) TOÀN HỆ THỐNG trong tháng vượt trần này → NGỪNG gọi Google (gợi ý vẫn dùng từ điển đã cache, không lỗi).\n\n0 hoặc để trống = KHÔNG giới hạn.\nGoogle FREE 10.000 Place Details/tháng — nên đặt ~9000 để an toàn dưới free tier.',
              role: ['owner', 'admin']},
            {key: 'geo_client_monthly_alert', label: 'Ngưỡng cảnh báo / client / tháng', type: 'number', unit: 'lượt/tháng',
              help: 'Nếu MỘT client dùng vượt số lượt Place Details này trong tháng → gửi CẢNH BÁO lên kênh tech (alert-tech) để phát hiện khách lạm dụng key chung.\n\n0 hoặc để trống = KHÔNG cảnh báo.\nVD: 2000 (1 khách bình thường hiếm khi tra >2000 địa chỉ mới/tháng).',
              role: ['owner', 'admin']},
            {key: 'geo_suggest_blocked_clients', label: 'Chặn gợi ý theo client', type: 'textarea',
              help: 'Danh sách Client ID bị CHẶN gợi ý địa chỉ (cách nhau dấu phẩy hoặc xuống dòng).\nClient trong danh sách → KHÔNG gọi Google/Goong, ô địa chỉ nhập text bình thường (chống đốt tiền khi khách lạm dụng).\n\nCopy Client ID từ cảnh báo alert-tech dán vào đây. Để trống = không chặn ai.',
              role: ['owner', 'admin']},
          ]
        },
      ]
    }
  },
  i18n: {
    messages: {
      vi: {
        title: 'Tham số hệ thống',
        searchPlaceholder: 'Tìm theo tên hoặc mã...',
        clear: 'Xoá',
        label: 'Tên tham số',
        key: 'Mã',
        value: 'Giá trị',
        found: 'Đang hiện {0}',
        notSet: 'Chưa đặt',
        on: 'Bật',
        off: 'Tắt',
        save: 'Lưu',
        cancel: 'Huỷ',
        create: 'Thêm tham số mới',
        advanced: 'Tham số nâng cao',
        showAdvanced: 'Hiện tham số nâng cao ({0})',
        hideAdvanced: 'Ẩn tham số nâng cao',
      },
      en: {
        title: 'System parameters',
        searchPlaceholder: 'Search by name or key...',
        clear: 'Clear',
        label: 'Parameter',
        key: 'Key',
        value: 'Value',
        found: 'Showing {0}',
        notSet: 'Not set',
        on: 'On',
        off: 'Off',
        save: 'Save',
        cancel: 'Cancel',
        create: 'Create new parameter',
        advanced: 'Advanced parameters',
        showAdvanced: 'Show advanced ({0})',
        hideAdvanced: 'Hide advanced',
      }
    }
  },
  computed: {
    totalDefined() {
      const role = this.$store.state.user.role;
      return this.groups
        .filter(g => role !== 'account' || g.wesignActions)
        .reduce((s, g) => s + g.items.filter(i => !i.role || i.role.includes(role)).length, 0);
    },
    visibleGroups() {
      const role = this.$store.state.user.role;
      const kw = (this.search || '').toLowerCase().trim();
      return this.groups
        // account chỉ được vào Tham số để lấy SID MISA — không thấy nhóm cấu hình khác
        .filter(g => role !== 'account' || g.wesignActions)
        .map(g => ({
          ...g,
          items: g.items.filter(item => {
            if (item.role && !item.role.includes(role)) return false;
            if (!kw) return true;
            return item.label.toLowerCase().includes(kw) || item.key.toLowerCase().includes(kw);
          })
        }))
        .filter(g => g.items.length);
    },
    visibleCount() {
      return this.visibleGroups.reduce((s, g) => s + g.items.length, 0);
    },
    knownKeySet() {
      const set = new Set();
      this.groups.forEach(g => g.items.forEach(i => set.add(i.key)));
      return set;
    },
    unknownItems() {
      if (this.$store.state.user.role === 'account') return [];
      return this.items.filter(i => !this.knownKeySet.has(i.key) && !this.hiddenKeys.includes(i.key));
    },
    canCreateRaw() {
      return ['owner', 'admin', 'tech'].includes(this.$store.state.user.role);
    },
    canGetSid() {
      return ['owner', 'account'].includes(this.$store.state.user.role);
    }
  },
  methods: {
    load() {
      let loader = this.$loading.show();
      this.$service.get(this.service, {params: {page: 1, pageSize: 500}}).then(response => {
        this.items = response.data.docs || [];
        this.rebuildDraft();
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.message) {
          this.$swal({text: error.response.data.message, icon: 'error'});
        }
      }).finally(() => loader.hide());
    },
    loadGeoUsage() {
      this.$service.get('/geocode-usage').then(r => { this.geoUsage = r.data; }).catch(e => console.warn('loadGeoUsage', e && e.message));
    },
    rebuildDraft() {
      const draft = {};
      this.items.forEach(it => {
        draft[it.key] = it.value == null ? '' : String(it.value);
      });
      this.groups.forEach(g => g.items.forEach(item => {
        if (draft[item.key] == null) {
          draft[item.key] = item.type === 'boolean' ? '0' : '';
        }
      }));
      this.draft = draft;
    },
    findStored(key) {
      return this.items.find(i => i.key === key);
    },
    hasStored(item) {
      return !!this.findStored(item.key);
    },
    originalValue(item) {
      const stored = this.findStored(item.key);
      if (stored) return String(stored.value);
      return item.type === 'boolean' ? '0' : '';
    },
    isDirty(item) {
      const cur = this.draft[item.key] == null ? '' : String(this.draft[item.key]);
      return cur !== this.originalValue(item);
    },
    canEdit(item) {
      if (item.readonly) return false;
      if (!item.role) return true;
      return item.role.includes(this.$store.state.user.role);
    },
    // Mirror backend etc.js: key nhạy cảm CHỈ owner được xoá/sửa (credential, API key).
    isOwnerOnlyKey(key) {
      if (!key) return false;
      const ownerOnlyKeys = ['phonenet_data_token', 'google_maps_api_key', 'goong_api_key'];
      const ownerOnlyPrefixes = ['db-monitor-', 'homer_', 'postmark_', 'athena_'];
      return ownerOnlyKeys.includes(key) || ownerOnlyPrefixes.some(p => key.startsWith(p));
    },
    // Quyền xoá = khớp guard backend DELETE /etc/:id: role owner/admin/tech + key owner-only chỉ owner.
    canDelete(item) {
      if (!this.canEdit(item) || !this.hasStored(item)) return false;
      const role = this.$store.state.user.role;
      if (!['owner', 'admin', 'tech'].includes(role)) return false;
      if (this.isOwnerOnlyKey(item.key) && role !== 'owner') return false;
      return true;
    },
    readonlyItems(group) {
      return (group.items || []).filter(i => i.readonly);
    },
    editableItems(group) {
      return (group.items || []).filter(i => !i.readonly);
    },
    formatReadonlyValue(item, value) {
      if (!value) return '';
      const v = String(value);
      // Timestamp ms → date readable
      if (item.key === 'esign-admin-token-expire-time') {
        const n = Number(v);
        if (!n || n <= 0) return v;
        const d = new Date(n);
        const left = n - Date.now();
        const leftStr = left > 0
          ? ` (còn ${Math.round(left / 60000)} phút)`
          : ' (đã hết hạn)';
        return d.toLocaleString('vi-VN') + leftStr;
      }
      // Token / secret → mask middle
      if (v.length > 32) {
        return v.substring(0, 12) + '…' + v.substring(v.length - 6) + `  (${v.length} ký tự)`;
      }
      return v;
    },
    onBoolChange(item, checked) {
      this.$set(this.draft, item.key, checked ? '1' : '0');
      if (this.isDirty(item)) this.saveItem(item);
    },
    // ---- type 'datetime': UI chọn ngày giờ, Etc LƯU epoch ms (chuỗi số) ----
    // ms -> chuỗi cho <input type="datetime-local"> theo GIỜ ĐỊA PHƯƠNG của admin.
    // Giá trị khoá "_auto" (do phonenet-data tự đo, không nằm trong nhóm hiển thị nào).
    autoValue(item) {
      const row = this.items.find(i => i.key === item.auto);
      const n = row ? parseInt(row.value, 10) : NaN;
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    fmtMs(ms) {
      const d = new Date(Number(ms));
      const p = (x) => String(x).padStart(2, '0');
      return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    msToLocalInput(ms) {
      const n = parseInt(ms, 10);
      if (!Number.isFinite(n) || n <= 0) return '';
      const d = new Date(n);
      const p = (x) => String(x).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    // chuỗi datetime-local -> ms. Rỗng/không hợp lệ -> '' (coi như chưa cấu hình → hệ thống chạy như cũ).
    onDateTimeChange(item, val) {
      if (!val) { this.$set(this.draft, item.key, ''); return; }
      const ms = new Date(val).getTime();
      this.$set(this.draft, item.key, Number.isFinite(ms) ? String(ms) : '');
    },
    resetItem(item) {
      this.$set(this.draft, item.key, this.originalValue(item));
    },
    saveItem(item) {
      const value = this.draft[item.key];
      if (value === '' || value == null) {
        // Xoá trắng 1 param đã lưu = xoá param → về mặc định (nếu có quyền xoá). Đồng nhất với nút thùng rác.
        if (this.hasStored(item) && this.canDelete(item)) { this.delItem(item); return; }
        this.$swal({text: this.$t('notSet'), icon: 'warning'});
        return;
      }
      const payload = {key: item.key, label: item.label, value: String(value)};
      const stored = this.findStored(item.key);
      let loader = this.$loading.show();
      const req = stored
        ? this.$service.put(this.service + '/' + stored._id, payload)
        : this.$service.post(this.service, payload);
      req.then(response => {
        if (response.data) {
          this.$swal({title: this.$t('updated'), icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
          this.load();
        } else {
          this.$swal({title: this.$t('error'), text: this.$t('updateFailed'), icon: 'error'});
        }
      }).catch(error => {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || this.$t('updateFailed'), icon: 'error'});
      }).finally(() => loader.hide());
    },
    saveRaw(item) {
      const payload = {key: item.key, label: item.label, value: String(item.value || '')};
      let loader = this.$loading.show();
      this.$service.put(this.service + '/' + item._id, payload).then(response => {
        if (response.data) {
          this.$swal({title: this.$t('updated'), icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
          this.load();
        }
      }).catch(error => {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || this.$t('updateFailed'), icon: 'error'});
      }).finally(() => loader.hide());
    },
    delRaw(id) {
      this.$swal({
        title: this.$t('deleteConfirm'),
        text: this.$t('deleteAlert'),
        icon: 'warning',
        showCancelButton: true
      }).then(result => {
        if (!result.value) return;
        let loader = this.$loading.show();
        this.$service.delete(this.service + '/' + id).then(response => {
          if (response.data) {
            this.$swal({title: this.$t('deleted'), icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
            this.load();
          }
        }).finally(() => loader.hide());
      });
    },
    // Xoá 1 tham số đã cài → hệ thống trở về giá trị mặc định (VD: trần/ngưỡng rỗng = không giới hạn, key rỗng = tắt gợi ý).
    delItem(item) {
      if (!this.canDelete(item)) return;
      const stored = this.findStored(item.key);
      if (!stored) return;
      this.$swal({
        title: this.$t('deleteConfirm'),
        text: this.$t('deleteAlert'),
        icon: 'warning',
        showCancelButton: true
      }).then(result => {
        if (!result.value) return;
        let loader = this.$loading.show();
        this.$service.delete(this.service + '/' + stored._id).then(response => {
          if (response.data) {
            this.$swal({title: this.$t('deleted'), icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
            this.load();
          } else {
            this.$swal({title: this.$t('error'), text: this.$t('updateFailed'), icon: 'error'});
          }
        }).catch(error => {
          const msg = error.response && error.response.data && error.response.data.message;
          this.$swal({text: msg || this.$t('updateFailed'), icon: 'error'});
        }).finally(() => loader.hide());
      });
    },
    openCreate() {
      this.formModel = {key: '', label: '', value: ''};
      this.formErrors = [];
      this.showForm = true;
    },
    saveNew(evt) {
      if (evt) evt.preventDefault();
      let loader = this.$loading.show();
      this.$service.post(this.service, this.formModel).then(response => {
        if (response.data) {
          this.showForm = false;
          this.$swal({title: this.$t('created'), icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
          this.load();
        }
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.message) {
          this.$swal({text: error.response.data.message, icon: 'error'});
        } else {
          this.formErrors = error.response.data;
        }
      }).finally(() => loader.hide());
    },
    // ===== MISA WeSign actions =====
    openGetSidModal() {
      // Flow MISA chốt: admin paste URL Google → BE parse sid + đổi ngay sang Access Token
      // (sid TTL chỉ 1 phút theo docs MISA mục API lấy Token)
      this.$refs.getSidModal.open();
    },
    onGetSidDone(success) {
      if (success) {
        this.load();  // reload Etc → access token + refresh token hiện trong readonly fields
      }
    },
  },
  mounted() {
    this.load();
    this.loadGeoUsage();
  }
}
</script>

<style scoped>
.tile-title {
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
}

.etc-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f3f3f3;
}

.etc-row:last-child {
  border-bottom: none;
}

.etc-row__label {
  flex: 0 0 38%;
  min-width: 0;
}

.etc-row__label-text {
  font-weight: 500;
  color: #333;
}

.etc-row__label-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.etc-row__control {
  flex: 1 1 auto;
  min-width: 0;
}

.etc-row__preview {
  margin-top: 4px;
  font-size: 12px;
  color: #28a745;
}

.etc-row__action {
  flex: 0 0 auto;
  align-self: center;
  min-width: 90px;
  text-align: right;
}

/* Compact list for readonly fields — 1 dòng/row, không grid 3 cột */
.etc-readonly-list {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 8px 12px;
  border: 1px solid #eee;
}
.etc-readonly-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 3px 0;
  font-size: 12.5px;
  line-height: 1.4;
}
.etc-readonly-item + .etc-readonly-item {
  border-top: 1px dashed #e9ecef;
}
.etc-readonly-item__label {
  flex: 0 0 auto;
  color: #6c757d;
  min-width: 180px;
}
.etc-readonly-item__value {
  flex: 1 1 auto;
  word-break: break-all;
  color: #212529;
}
</style>
